import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Space, Typography } from 'antd';

import ConditionBuilder, { buildConditionFields, parseConditions } from '../common/ConditionBuilder';
import FilterIdsLine from '../common/FilterIdsLine';
import { entryColumnFields, findField, isEntriesCmp, queryUsesMe } from '../../utils/kqlBuilder';
import { isGroupItem } from '../../utils/conditionGroups';
import { DRIVEN_ME_PROBLEM, conditionSentence, drivenFilterProblem } from '../../utils/drivenSummary';
import { copyTextToClipboard } from '../../utils/clipboard';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * "Only those where" — which of the linked items a driven value reads
 * (KPR-950, KPE-010; docs/platform-standards.md §15, "Setup is picked, not
 * typed").
 *
 * The stored form is KQL with `#id`s (`options.drivenFrom.where.filter`), and
 * it does not change. Nobody has to read or write it: the filter is built with
 * the shared ConditionBuilder over the source card's elements (choices as
 * pills, an item picked by its title, a date from a calendar), and the text is
 * one click away, behind **Edit as text**, with every `#id` in it explained
 * beneath (FilterIdsLine) and a Copy button.
 *
 * Three ways to show what is stored, decided from the text:
 *
 *   rows      it is a set of conditions the builder shows (the default, and
 *             what an empty filter opens as);
 *   sentence  it reads, but the rows cannot show it — a group inside a group
 *             or `not (a and b)` (the rows show all / any of these and one
 *             level of groups, KPR-951), a list on an element that offers
 *             none, an element that is not on the source card. It opens as a
 *             read-only plain sentence with
 *             Edit as text, and nothing is rewritten unless the person edits
 *             it (a builder that re-serialized what it understood would delete
 *             the rest of the query);
 *   text      the person asked for it, or the stored text does not read at
 *             all (then the problem is said under it).
 *
 * A stored filter is never written by being opened: what goes to `onChange`
 * is only ever the builder's rows after an edit, or the person's typing.
 */

/** Does every row name something on the source card (or a system field)? */
function rowsAreKnown(rows, fields) {
	return (rows || []).every(row => {
		// A group of conditions (KPR-951): every row in it.
		if (isGroupItem(row)) return rowsAreKnown(row.rows || [], fields);
		const field = findField(fields, row.path, row.forceElement);
		if (!field || field.unknown) return false;
		if (isEntriesCmp(row.cmp)) return rowsAreKnown(row.entryRows || [], entryColumnFields(field));
		return true;
	});
}

/** Which view a filter opens in: 'rows', 'sentence' or 'text'. */
export function whereModeFor(text, conditionFields) {
	const trimmed = String(text || '').trim();
	if (!trimmed) return 'rows';
	const parsed = parseConditions(trimmed, conditionFields);
	if (parsed.error) return 'text';
	if (parsed.rows === null || !rowsAreKnown(parsed.rows, conditionFields)) return 'sentence';
	return 'rows';
}

export default function DrivenWhereEditor({
	value = '',
	onChange,
	// The source card's elements (merged, with their ids): the builder's vocabulary.
	elements = [],
	// The source card's and the cards beneath it, as filter fields (kqlBuilder
	// buildFilterFields): what each id in the text names, and the sentence's words.
	idFields = [],
	collectionId = null,
	// A problem found at Save (DrivenFromModal), said under the text.
	problem = null,
	emptyText = 'Leave it empty to count them all.',
}) {
	const conditionFields = useMemo(() => buildConditionFields(elements), [elements]);
	const [mode, setMode] = useState(() => whereModeFor(value, conditionFields));
	// Text because the person asked for it (never flipped back for them).
	const [asked, setAsked] = useState(false);
	const [copied, setCopied] = useState(''); // '' | 'copied' | 'failed'

	// What we last handed to onChange: the parent echoes it back as `value`,
	// and that echo must not choose the view again.
	const lastEmitted = useRef(null);
	// The text the PARENT last handed us.
	const received = useRef(value);
	const fieldsRef = useRef(conditionFields);
	fieldsRef.current = conditionFields;

	useEffect(() => {
		if (value === lastEmitted.current) return;
		received.current = value;
		setAsked(false);
		setMode(whereModeFor(value, fieldsRef.current));
	}, [value]);

	// The source card's elements can arrive after the text (an `is any of` is a
	// row only against an element that offers it): a filter that opened as a
	// sentence for want of them opens as rows once they are here. Only the
	// parent's own text, and never a view the person asked for.
	const viewState = useRef({});
	viewState.current = { mode, asked, value };
	useEffect(() => {
		const { mode: shown, asked: chosen, value: text } = viewState.current;
		if (chosen || shown !== 'sentence' || text !== received.current) return;
		if (whereModeFor(text, conditionFields) === 'rows') setMode('rows');
	}, [conditionFields]);

	const emit = useCallback(next => {
		lastEmitted.current = next;
		if (typeof onChange === 'function') onChange(next);
	}, [onChange]);

	const openText = () => { setAsked(true); setMode('text'); };
	const fits = mode === 'text' && whereModeFor(value, conditionFields) === 'rows';
	const liveProblem = mode === 'text' ? (problem || drivenFilterProblem(value)) : problem;

	const handleCopy = async () => {
		const ok = await copyTextToClipboard(String(value || ''));
		setCopied(ok ? 'copied' : 'failed');
	};
	useEffect(() => {
		if (!copied) return undefined;
		const timer = setTimeout(() => setCopied(''), 2000);
		return () => clearTimeout(timer);
	}, [copied]);

	if (mode === 'rows') {
		return (
			<div className="driven-where">
				<ConditionBuilder
					value={value}
					onChange={emit}
					elements={elements}
					collectionId={collectionId}
					emptyText={emptyText}
					onEditAsText={openText}
				/>
				{problem ? <Typography.Text type="danger" className="d-block">{problem}</Typography.Text> : null}
			</div>
		);
	}

	if (mode === 'sentence') {
		const sentence = conditionSentence(value, idFields);
		return (
			<div className="driven-where driven-where--sentence">
				<p className="driven-where__sentence" aria-label="The condition">{sentence ? `Where ${sentence}.` : ''}</p>
				<Typography.Text type="secondary" className="d-block">
					The builder can’t show this condition as rows. It is kept exactly as it is unless you change it.
				</Typography.Text>
				{/* `me` (KPR-143): a driven value is one number for everyone, so the
				    builder here never offers it, and a stored one is said, not dropped. */}
				{queryUsesMe(value) ? <Typography.Text type="warning" className="d-block">{DRIVEN_ME_PROBLEM}</Typography.Text> : null}
				<Space wrap className="driven-where__actions">
					<Button onClick={openText}>Edit as text</Button>
					<Button type="link" onClick={() => { setAsked(false); setMode('rows'); emit(''); }}>
						Clear it and use the builder
					</Button>
				</Space>
				{problem ? <Typography.Text type="danger" className="d-block">{problem}</Typography.Text> : null}
			</div>
		);
	}

	return (
		<div className="driven-where driven-where--text">
			<label htmlFor="drivenFromWhereText" className="d-block">The condition as text</label>
			<Input.TextArea
				{...NO_TEXT_ASSIST}
				id="drivenFromWhereText"
				className="driven-where__text"
				autoSize={{ minRows: 2, maxRows: 8 }}
				value={value}
				status={liveProblem ? 'error' : undefined}
				aria-invalid={liveProblem ? 'true' : undefined}
				aria-describedby={liveProblem ? 'drivenFromWhereProblem' : undefined}
				onChange={event => emit(event.target.value)}
			/>
			{liveProblem ? (
				<Typography.Text type="danger" id="drivenFromWhereProblem" className="d-block" role="alert">{liveProblem}</Typography.Text>
			) : null}
			{/* Saved, the text names the source card's elements by id (element ids
			    step 3): each said in words beneath. */}
			<FilterIdsLine text={value} fields={idFields} />
			<Space wrap className="driven-where__actions">
				{fits ? (
					<Button onClick={() => { setAsked(false); setMode('rows'); }}>Back to the builder</Button>
				) : null}
				{String(value || '').trim() ? <Button onClick={handleCopy}>{copied === 'copied' ? 'Copied' : (copied === 'failed' ? 'Couldn’t copy' : 'Copy')}</Button> : null}
			</Space>
			{!fits && !liveProblem && String(value || '').trim() ? (
				<Typography.Text type="secondary" className="d-block">
					The builder can’t show this condition as rows, so it stays as text.
				</Typography.Text>
			) : null}
		</div>
	);
}
